import 'css/src/data/transcript/nodes/OverlayElementNode';

import type { LexicalNode, NodeKey, SerializedElementNode, Spread } from 'lexical';
import { ElementNode } from 'lexical';

import { $createBaseOverlayElementNode } from 'js/src/data/transcript/nodes/BaseOverlayElementNode';
import {
    $createDragHandleElementNode,
    DragHandleType,
} from 'js/src/data/transcript/nodes/DragHandleElementNode';
import { InvalidOverlayError } from 'js/src/data/transcript/transcript-error';

export const OVERLAY_ELEMENT_NODE_CLASS_NAME = 'overlay-container';

export enum OverlayState {
    Default = 'DEFAULT',
    Edit = 'EDIT',
    TimingMode = 'TIMING_MODE',
}

const OverlayElementNodeLexicalType = 'insert-node'; // DO NOT CHANGE THIS OR ELSE TRANSCRIPT WILL NOT RENDER
export type SerializedOverlayElementNode = Spread<
    {
        sceneId: string;
        state: OverlayState;
        type: typeof OverlayElementNodeLexicalType;
        version: 1;
    },
    SerializedElementNode
>;

/**
 * OverlayElementNode is an ElementNode that consists of a BaseOverlayElementNode
 * and two DragHandleElementNodes (which may or may not be visible)
 * Overlays should only be instantiated via `$createOverlayElementNode`
 *
 *
 * In a transcript we could have the following tree structure:
 *                                              [ Root ]
 *    [TrimElement]          [Word 6...Word 12]              [OverlayElement]                        [TrimElement]
 * [...words left of trim]                         [Drag,       BaseOverlay,        Drag]          [...words right of trim]
 *                                               [ SceneNumberNode ...words inside overlay]
 *
 * words left/right of trim means those word nodes are trimmed out of the transcript and do not appear to the user
 * but user can un-trim to re-reveal those words, that's why we still maintain them inside lexical
 *
 */
export class OverlayElementNode extends ElementNode {
    __state: OverlayState;
    __sceneId: string;

    constructor(sceneId: string, state?: OverlayState, key?: NodeKey) {
        super(key);
        this.__sceneId = sceneId;
        this.__state = state || OverlayState.Default;
    }

    static override getType(): string {
        return 'insert-node'; // THIS HAS TO BE SAME AS THE TYPE IN `SerializedOverlayElementNode`
    }

    static override clone(node: OverlayElementNode): OverlayElementNode {
        return new OverlayElementNode(node.__sceneId, node.__state, node.__key);
    }

    override createDOM(): HTMLElement {
        const dom = document.createElement('span');
        this.__update(dom);
        return dom;
    }

    override updateDOM(prevNode: OverlayElementNode, dom: HTMLElement): boolean {
        if (prevNode.__state !== this.__state) {
            this.__update(dom);
        }
        return false;
    }

    static override importJSON(serializedNode: SerializedOverlayElementNode): OverlayElementNode {
        const node = new OverlayElementNode(serializedNode.sceneId, serializedNode.state);
        return node;
    }

    override exportJSON(): SerializedOverlayElementNode {
        return {
            ...super.exportJSON(),
            sceneId: this.sceneId,
            state: this.getState(),
            type: OverlayElementNodeLexicalType,
            version: 1,
        };
    }

    __update(domElement: HTMLElement) {
        const classNames = [OVERLAY_ELEMENT_NODE_CLASS_NAME];
        switch (this.__state) {
            case OverlayState.Default:
                classNames.push('default');
                break;
            case OverlayState.Edit:
                classNames.push('edit');
                break;
            case OverlayState.TimingMode:
                classNames.push('timing-mode');
                break;
        }
        domElement.className = classNames.join(' ');
    }

    get sceneId() {
        return this.__sceneId;
    }

    getState() {
        const self = this.getLatest();
        return self.__state;
    }

    setState(newState: OverlayState) {
        const self = super.getWritable();
        self.__state = newState;
        return self;
    }

    setSceneId(newSceneId: string) {
        const self = super.getWritable();
        self.__sceneId = newSceneId;
        return self;
    }
}

export function $createOverlayElementNode(
    sceneId: string,
    nodes: Array<LexicalNode>,
    sceneNumber: number,
    state?: OverlayState,
): OverlayElementNode {
    if (!nodes) {
        throw new InvalidOverlayError('Cannot have an empty overlay');
    }
    const overlay = new OverlayElementNode(sceneId, state);
    const baseOverlay = $createBaseOverlayElementNode(sceneNumber, {});
    nodes.forEach((node) => {
        baseOverlay.append(node);
    });
    overlay.append(
        $createDragHandleElementNode(DragHandleType.Left),
        baseOverlay,
        $createDragHandleElementNode(DragHandleType.Right),
    );
    return overlay;
}

export function $isOverlayElementNode(node: LexicalNode | null): node is OverlayElementNode {
    return node instanceof OverlayElementNode;
}
